import React, { useEffect, useState, useContext, useMemo } from "react";
import { useForm, useController } from "react-hook-form";
import { CommonModal } from "../../AbstractElements";
import { Form } from "reactstrap";
import { Info } from "react-feather";
import { Tabs, Tab, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { getCarTypes } from "../../Services/Shared.service";
import { offerRewardConstraints } from "../../Services/Offer.service";
import SharedDataContext from "../../_helper/SharedData";
import {
  rideTypeOptions,
  bookingSourceOptions,
  discountApplyOnOptions,
  rewardTypeOptions,
  paymentTypeOptions,
  CITY_TO_AIRPORT_API_VALUES,
} from "../../Constant/offerConstants";
import BasicInfoTab from "./BasicInfoTab";
import ConditionsTab from "./ConditionsTab";
import ConfirmationModal from "./ConfirmationModal";
import moment from "moment";

// ===== Default Form Values Factory =====
const getEmptyFormValues = () => {
  const now = new Date();
  const oneMonthLater = new Date(now);
  oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
  return {
    name: "",
    offerCode: "",
    serviceCityId: null,
      carType: [],
      rewardType: rewardTypeOptions.length === 1 ? rewardTypeOptions[0] : null,
      rewardAmount: "",
      usageLimit: "",
      validFrom: now,
      validUpto: oneMonthLater,
      targetRideType: [],
      targetBookingSource: [],
      triggerPaymentType: [],
      rideType: [],
      rewardBookingSource: [],
      rewardPaymentType: [],
      rewardDiscountApplyOn:
        discountApplyOnOptions.length === 1 ? discountApplyOnOptions[0] : null,
      triggerMinDistance: "",
      rewardMinDistance: "",
      isDriverWalletCredit: false,
      requiresClaim: false,
    };
  };

const OfferDialog = ({ isOpen, toggler, offerObj, onSave }) => {
  const { cityData } = useContext(SharedDataContext);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors, isDirty, isSubmitting },
  } = useForm({ defaultValues: getEmptyFormValues(), mode: "onChange" });

  const [carTypeOptions, setCarTypeOptions] = useState([]);
  const [hasUsageLimit, setHasUsageLimit] = useState(false);
  const [rewardConstraints, setRewardConstraints] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const [showExpiredSaveWarning, setShowExpiredSaveWarning] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // ===== Shared Validators =====
  const createRideTypeValidator = (bookingSourceFieldName) => (val) => {
    const bookingSources = getValues(bookingSourceFieldName);
    if (!bookingSources || bookingSources.length === 0) return true;
    const hasPartner = bookingSources.some((s) => s.value === "Partner");
    const isOnlyKiosk =
      bookingSources.length === 1 && bookingSources[0].value === "Kiosk";
    if (val && Array.isArray(val)) {
      if (isOnlyKiosk && val.some((r) => r.value === "CityToAirport"))
        return "City to Airport is not available for Kiosk";
      // if (!hasPartner && val.some((r) => r.value === "KurbPickup"))
      //   return "KurbPickup is only available for Driver App";
    }
    return true;
  };

  const validateMinDistance = (val) => {
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(val))
      return "Max 3 digits (999) and up to 2 decimals allowed";
    if (
      rewardConstraints?.minDistance != null &&
      parseFloat(val) < rewardConstraints.minDistance
    )
      return `Minimum distance is ${rewardConstraints.minDistance}`;
    return true;
  };

  // ===== Controllers: Basic Info =====
  const nameField = useController({
    name: "name",
    control,
    rules: {
      required: "Offer Name is required",
      maxLength: {
        value: 100,
        message: "Offer Name cannot exceed 100 characters",
      },
    },
  }).field;
  const offerCodeField = useController({
    name: "offerCode",
    control,
    rules: {
      required: "Offer Code is required",
      maxLength: {
        value: 25,
        message: "Offer Code cannot exceed 25 characters",
      },
      validate: (val) => {
        if (val && /\s/.test(val)) return "Offer Code must not contain spaces";
        return true;
      },
    },
  }).field;
  const serviceCityIdField = useController({
    name: "serviceCityId",
    control,
    rules: { required: "Service City is required" },
  }).field;
  const carTypeField = useController({
    name: "carType",
    control,
    rules: { required: "Car Type is required" },
  }).field;
  const rewardTypeField = useController({
    name: "rewardType",
    control,
    rules: { required: "Reward Type is required" },
  }).field;
  const rewardAmountField = useController({
    name: "rewardAmount",
    control,
    rules: {
      required: "Reward Amount is required",
      validate: (val) => {
        if (val !== "" && !/^\d+(\.\d{1,2})?$/.test(String(val)))
          return "Only up to 2 decimal places allowed";
        const rewardType = getValues("rewardType")?.value;
        const discountOn = getValues("rewardDiscountApplyOn")?.value;
        const amount = parseFloat(val);
        if (isNaN(amount)) return true;
        if (amount <= 0) return "Reward amount must be greater than 0";
        if (rewardType === "P") {
          if (amount > 100) return "Maximum percentage is 100%";
        }
        else if (rewardType === "F" && rewardConstraints) {
          if (
            discountOn === "ADDNL_PER_KM_CHARGE" &&
            rewardConstraints.minPerKmCharge != null
          ) {
            if (amount > rewardConstraints.minPerKmCharge) {
              return `Maximum limit is ${rewardConstraints.minPerKmCharge}`;
            }
          }
        }
        return true;
      },
    },
  }).field;
  const usageLimitField = useController({
    name: "usageLimit",
    control,
    rules: {
      validate: (val) => {
        if (hasUsageLimit) {
          if (!val || val === "") return "Usage Limit is required";
          const numVal = Number(val);
          if (numVal <= 0) return "Usage Limit must be greater than 0";
          if (!Number.isInteger(numVal)) return "Usage Limit cannot contain decimals";
          if (String(val).length > 5) return "Usage Limit cannot exceed 5 digits";
        }
        return true;
      },
    },
  }).field;
  const validFromField = useController({
    name: "validFrom",
    control,
    rules: {
      required: "Valid From is required",
      validate: (val) => {
        if (!val) return true;
        const selectedDate = new Date(val);

        // Allow if editing an existing offer and the date wasn't changed from its original past value
        if (offerObj && offerObj.validFrom) {
          const origDate = new Date(offerObj.validFrom);
          if (
            origDate < new Date() &&
            selectedDate.getTime() === origDate.getTime()
          ) {
            return true;
          }
        }

        if (selectedDate < new Date()) {
          return "Valid From cannot be in the past";
        }
        return true;
      },
    },
  }).field;
  const validUptoField = useController({
    name: "validUpto",
    control,
    rules: {
      required: "Valid Upto is required",
      validate: (val) => {
        const from = validFromField.value;
        if (from && val) {
          const fromDate = new Date(from);
          const uptoDate = new Date(val);

          if (uptoDate.getTime() <= fromDate.getTime()) {
            return "Valid Upto must be after Valid From";
          }
        }
        return true;
      },
    },
  }).field;
  const rewardDiscountApplyOnField = useController({
    name: "rewardDiscountApplyOn",
    control,
    rules: { required: "Reward Discount Apply On is required" },
  }).field;
  const requiresClaimField = useController({
    name: "requiresClaim",
    control,
  }).field;
  const isDriverWalletCreditField = useController({
    name: "isDriverWalletCredit",
    control,
  }).field;

  // ===== Controllers: Trigger Condition =====
  const targetRideTypeField = useController({
    name: "targetRideType",
    control,
    rules: {
      required: "Ride Type is required",
      validate: createRideTypeValidator("targetBookingSource"),
    },
  }).field;
  const targetBookingSourceField = useController({
    name: "targetBookingSource",
    control,
    rules: { required: "Booking Source is required" },
  }).field;
  const triggerPaymentTypeField = useController({
    name: "triggerPaymentType",
    control,
    rules: { required: "Payment Type is required" },
  }).field;
  const triggerMinDistanceField = useController({
    name: "triggerMinDistance",
    control,
    rules: {
      required: "Min distance is required",
      validate: validateMinDistance,
    },
  }).field;

  // ===== Controllers: Reward Condition =====
  const rideTypeField = useController({
    name: "rideType",
    control,
    rules: {
      required: "Ride Type is required",
      validate: createRideTypeValidator("rewardBookingSource"),
    },
  }).field;
  const rewardBookingSourceField = useController({
    name: "rewardBookingSource",
    control,
    rules: { required: "Booking Source is required" },
  }).field;
  const rewardPaymentTypeField = useController({
    name: "rewardPaymentType",
    control,
    rules: { required: "Payment Type is required" },
  }).field;
  const rewardMinDistanceField = useController({
    name: "rewardMinDistance",
    control,
    rules: {
      required: "Min distance is required",
      validate: validateMinDistance,
    },
  }).field;

  // ===== Effects =====
  useEffect(() => {
    if (isOpen) {
      setActiveTab(0);
      offerRewardConstraints()
        .then((res) => {
          if (res?.data) {
            setRewardConstraints(res.data);
          }
        })
        .catch((err) =>
          console.error("Error fetching reward constraints:", err),
        );
    }
  }, [isOpen]);

  useEffect(() => {
    if (serviceCityIdField.value?.value) {
      getCarTypes({ serviceCityId: serviceCityIdField.value.value })
        .then((res) => {
          const data = res.data.map((el) => ({ label: el.name, value: el.id }));
          setCarTypeOptions(data);
          const currentCarTypes = getValues("carType");
          if (currentCarTypes && currentCarTypes.length > 0) {
            const needsHealing = currentCarTypes.some(
              (ct) => ct.label === ct.value,
            );
            if (needsHealing) {
              const healed = currentCarTypes.map((ct) => {
                const found = data.find(
                  (d) => String(d.value) === String(ct.value),
                );
                return found || ct;
              });
              setValue("carType", healed);
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching car types:", err);
          setCarTypeOptions([]);
        });
    } else {
      setCarTypeOptions([]);
    }
  }, [serviceCityIdField.value, getValues, setValue]);

  // Helper to map values to options
  const getValueFromOptions = (value, options) => {
    if (!value) return null;
    if (Array.isArray(value)) {
      return value.map((v) => {
        if (typeof v === "object" && v.value) return v;
        return (
          options.find((opt) => String(opt.value) === String(v)) || {
            value: v,
            label: v,
          }
        );
      });
    }
    if (typeof value === "object" && value.value) return value;
    return (
      options.find((opt) => String(opt.value) === String(value)) || {
        value: value,
        label: value,
      }
    );
  };

  // Helper to parse conditions array from API response
  const parseConditions = (conditions) => {
    const result = { trigger: {}, reward: {} };
    if (!conditions || !Array.isArray(conditions)) return result;
    conditions.forEach((cond) => {
      try {
        const parsed =
          typeof cond.conditionJson === "string"
            ? JSON.parse(cond.conditionJson)
            : cond.conditionJson;
        if (cond.conditionType === "TRIGGER") {
          result.trigger = parsed;
        } else if (cond.conditionType === "REWARD") {
          result.reward = parsed;
        }
      } catch (e) {
        console.error("Failed to parse conditionJson:", e);
      }
    });
    return result;
  };

  const dynamicServiceCityOptions = useMemo(() => {
    return cityData
      .filter((c) => c.value && c.value.isOfferEnabled === true)
      .map((c) => ({ label: c.value.name, value: c.value.id }));
  }, [cityData]);

  // Auto-select if only one city option and no value currently set
  useEffect(() => {
    if (
      dynamicServiceCityOptions.length === 1 &&
      !serviceCityIdField.value
    ) {
      serviceCityIdField.onChange(dynamicServiceCityOptions[0]);
    }
  }, [dynamicServiceCityOptions]);

  useEffect(() => {
    if (isOpen) {
      if (offerObj) {
        const { trigger: trig, reward } = offerObj.conditions
          ? parseConditions(offerObj.conditions)
          : {
              trigger: offerObj.triggerCondition || {},
              reward: offerObj.rewardCondition || {},
            };

        const serviceCityValue = Array.isArray(trig.service_city)
          ? trig.service_city[0]
          : trig.service_city || offerObj.serviceCityId;
        const city = dynamicServiceCityOptions.find(
          (c) => String(c.value) === String(serviceCityValue),
        );

        const hasLimit =
          offerObj.usageLimit != null && offerObj.usageLimit !== "";
        setHasUsageLimit(hasLimit);

        reset({
          name: offerObj.offerName || offerObj.name || "",
          offerCode: offerObj.offerCode || "",
          serviceCityId: city,
          carType: getValueFromOptions(
            trig.car_type || offerObj.carType,
            carTypeOptions,
          ),
          rewardType:
            getValueFromOptions(offerObj.rewardType, rewardTypeOptions) ||
            (rewardTypeOptions.length === 1 ? rewardTypeOptions[0] : null),
          rewardAmount: offerObj.rewardAmount || "",
          usageLimit: hasLimit ? offerObj.usageLimit : "",
          validFrom: offerObj.validFrom ? new Date(offerObj.validFrom) : null,
          validUpto: offerObj.validTo
            ? new Date(offerObj.validTo)
            : offerObj.validUpto
              ? new Date(offerObj.validUpto)
              : null,
          targetRideType: getValueFromOptions(
            normalizeRideTypes(trig.ride_type),
            rideTypeOptions,
          ),
          targetBookingSource: getValueFromOptions(
            trig.booking_source,
            bookingSourceOptions,
          ),
          triggerPaymentType: getValueFromOptions(
            normalizePaymentModeForPrefill(trig.payment_mode_type),
            paymentTypeOptions,
          ),
          rideType: getValueFromOptions(normalizeRideTypes(reward.ride_type), rideTypeOptions),
          rewardBookingSource: getValueFromOptions(
            reward.booking_source,
            bookingSourceOptions,
          ),
          rewardPaymentType: getValueFromOptions(
            normalizePaymentModeForPrefill(reward.payment_mode_type),
            paymentTypeOptions,
          ),
          rewardDiscountApplyOn:
            getValueFromOptions(
              reward.discount_apply_on,
              discountApplyOnOptions,
            ) ||
            (discountApplyOnOptions.length === 1
              ? discountApplyOnOptions[0]
              : null),
          triggerMinDistance: trig.min_distance ?? "",
          rewardMinDistance: reward.min_distance ?? "",
          requiresClaim: offerObj.requiresClaim ?? false,
          isDriverWalletCredit: offerObj.isDriverWalletCredit ?? false,
        });
      } else {
        setHasUsageLimit(false);
        reset(getEmptyFormValues());
      }
    }
  }, [isOpen, offerObj, reset]);

  const formatLocalDateTime = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // Expand CityToAirport into its API values, pass through others
  const expandRideTypes = (selections) =>
    selections?.flatMap((i) =>
      i.value === "CityToAirport" ? CITY_TO_AIRPORT_API_VALUES : [i.value]
    ) ?? [];

  // Collapse RideLater/KurbPickup from API back to CityToAirport for prefill
  const normalizeRideTypes = (apiRideTypes) => {
    if (!apiRideTypes || !Array.isArray(apiRideTypes)) return apiRideTypes;
    const hasCityToAirport =
      apiRideTypes.includes("RideLater") || apiRideTypes.includes("KurbPickup");
    let result = apiRideTypes.filter(
      (v) => v !== "RideLater" && v !== "KurbPickup"
    );
    if (hasCityToAirport) result.push("CityToAirport");
    return [...new Set(result)];
  };

  // Collapse CreditCustomer / CreditCard back to the combined option value for prefill
  const normalizePaymentModeForPrefill = (apiValues) => {
    if (!apiValues || !Array.isArray(apiValues)) return apiValues;
    const hasCreditTypes = apiValues.some(
      (v) => v === "CreditCustomer" || v === "CreditCard" ||
             (typeof v === "string" && v.includes("CreditCustomer"))
    );
    const result = apiValues.filter(
      (v) => v !== "CreditCustomer" && v !== "CreditCard"
    );
    if (hasCreditTypes) result.push('"CreditCustomer","CreditCard"');
    return result;
  };

  const onSubmit = async (data) => {
    const carTypeIds = data.carType?.map((i) => i.value);
    const serviceCityId = data.serviceCityId?.value;

    const submissionData = {
      id: offerObj ? offerObj.id : undefined,
      offerName: data.name?.trim(),
      offerCode: data.offerCode?.trim(),
      rewardType: data.rewardType?.value,
      rewardAmount: parseFloat(data.rewardAmount),
      usageLimit: hasUsageLimit ? parseInt(data.usageLimit) : null,
      requiresClaim: data.requiresClaim ?? false,
      isDriverWalletCredit: data.isDriverWalletCredit ?? false,
      isActive: true,
      validFrom: formatLocalDateTime(data.validFrom),
      validTo: formatLocalDateTime(data.validUpto),
      conditions: [
        {
          conditionType: "TRIGGER",
          conditionJson: JSON.stringify({
            ride_type: expandRideTypes(data.targetRideType),
            service_city: [serviceCityId],
            booking_source: data.targetBookingSource?.map((i) => i.value),
            car_type: carTypeIds,
            discount_apply_on: data.rewardDiscountApplyOn?.value,
            payment_mode_type: data.triggerPaymentType?.flatMap((i) => {
              try { return JSON.parse(`[${i.value}]`); }
              catch { return [i.value]; }
            }),
            min_distance: data.triggerMinDistance
              ? parseFloat(data.triggerMinDistance)
              : 0,
          }),
        },
        {
          conditionType: "REWARD",
          conditionJson: JSON.stringify({
            ride_type: expandRideTypes(data.rideType).filter(val => val !== "KurbPickup"),
            service_city: [serviceCityId],
            booking_source: data.rewardBookingSource?.map((i) => i.value),
            car_type: carTypeIds,
            discount_apply_on: data.rewardDiscountApplyOn?.value,
            payment_mode_type: data.rewardPaymentType?.flatMap((i) => {
              try { return JSON.parse(`[${i.value}]`); }
              catch { return [i.value]; }
            }),
            min_distance: data.rewardMinDistance
              ? parseFloat(data.rewardMinDistance)
              : 0,
          }),
        },
      ],
    };

    if (moment(submissionData.validTo).isBefore(moment())) {
       setPendingSubmitData(submissionData);
       setShowExpiredSaveWarning(true);
       return;
    }

    await executeSave(submissionData);
  };

  const executeSave = async (submissionData) => {
    setIsSaving(true);
    const success = await onSave(submissionData);
    setIsSaving(false);
    if (success) {
      setHasUsageLimit(false);
      reset(getEmptyFormValues());
      toggler();
    }
  };

  const handleConfirmExpiredSave = () => {
    setShowExpiredSaveWarning(false);
    if (pendingSubmitData) {
      executeSave(pendingSubmitData);
      setPendingSubmitData(null);
    }
  };

  const getMinDateForValidFrom = () => {
    if (offerObj && offerObj.validFrom) {
      const originalDate = new Date(offerObj.validFrom);
      return originalDate < new Date() ? originalDate : new Date();
    }
    return new Date();
  };

  const getMinTimeForValidFrom = () => {
    const from = validFromField.value;
    if (!from) return undefined;
    const fromDate = new Date(from);
    const today = new Date();
    if (
      fromDate.getFullYear() === today.getFullYear() &&
      fromDate.getMonth() === today.getMonth() &&
      fromDate.getDate() === today.getDate()
    ) {
      return today;
    }
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay;
  };

  // Compute minTime for validUpto when same day as validFrom
  const getMinTimeForValidUpto = () => {
    const from = validFromField.value;
    const upto = validUptoField.value;
    if (!from) return undefined;
    const fromDate = new Date(from);
    const uptoDate = upto ? new Date(upto) : null;
    if (
      uptoDate &&
      fromDate.getFullYear() === uptoDate.getFullYear() &&
      fromDate.getMonth() === uptoDate.getMonth() &&
      fromDate.getDate() === uptoDate.getDate()
    ) {
      return fromDate;
    }
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay;
  };

  const getMaxTimeForValidUpto = () => {
    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
  };

  // ===== Tab Navigation =====
  const basicInfoFieldNames = [
    "name",
    "offerCode",
    "serviceCityId",
    "carType",
    "rewardType",
    "rewardDiscountApplyOn",
    "rewardAmount",
    "validFrom",
    "validUpto",
    ...(hasUsageLimit ? ["usageLimit"] : []),
  ];

  const handleNext = async () => {
    const isValid = await trigger(basicInfoFieldNames);
    if (isValid) {
      setActiveTab(1);
    }
  };

  // ===== Close with Warning =====
  const handleCloseWithWarning = () => {
    if (isDirty) {
      setShowCloseWarning(true);
    } else {
      toggler();
    }
  };

  const confirmClose = () => {
    setShowCloseWarning(false);
    reset(getEmptyFormValues());
    toggler();
  };

  return (
    <CommonModal
      isOpen={isOpen}
      toggler={handleCloseWithWarning}
      title={offerObj ? "Edit Offer" : "Create Offer"}
      size="xl"
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
            "& .Mui-selected": { color: "#41CC7D" },
            "& .MuiTabs-indicator": { backgroundColor: "#41CC7D" },
          }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Basic Info
                {basicInfoFieldNames.some((field) => errors[field]) && (
                  <Info size={16} color="#dc3545" />
                )}
              </Box>
            }
            value={0}
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Conditions
                {[
                  "targetRideType",
                  "targetBookingSource",
                  "triggerPaymentType",
                  "triggerMinDistance",
                  "rideType",
                  "rewardBookingSource",
                  "rewardPaymentType",
                  "rewardMinDistance",
                ].some((field) => errors[field]) && (
                  <Info size={16} color="#dc3545" />
                )}
              </Box>
            }
            value={1}
          />
        </Tabs>

        <Box sx={{ pt: 2 }}>
          {activeTab === 0 && (
            <BasicInfoTab
              fields={{
                nameField,
                offerCodeField,
                serviceCityIdField,
                carTypeField,
                rewardTypeField,
                rewardDiscountApplyOnField,
                rewardAmountField,
                validFromField,
                validUptoField,
                usageLimitField,
                requiresClaim: requiresClaimField,
                isDriverWalletCredit: isDriverWalletCreditField,
              }}
              errors={errors}
              options={{
                cityOptions: dynamicServiceCityOptions,
                carTypeOptions,
                rewardTypeOptions,
                discountApplyOnOptions,
              }}
                hasUsageLimit={hasUsageLimit}
                setHasUsageLimit={setHasUsageLimit}
                trigger={trigger}
                dateHelpers={{
                  getMinTimeForValidUpto,
                  getMaxTimeForValidUpto,
                  getMinDateForValidFrom,
                  getMinTimeForValidFrom,
                }}
              />
          )}

          {activeTab === 1 && (
            <ConditionsTab
              triggerConfig={{
              bookingSourceField: targetBookingSourceField,
              bookingSourceName: "targetBookingSource",
              bookingSourceOptions: bookingSourceOptions,
              rideTypeField: targetRideTypeField,
              rideTypeName: "targetRideType",
              paymentTypeField: triggerPaymentTypeField,
              paymentTypeName: "triggerPaymentType",
              paymentTypeOptions: paymentTypeOptions,
              minDistanceField: triggerMinDistanceField,
              minDistanceName: "triggerMinDistance",
              tooltipId: "triggerMinDistanceInfo",
              }}
              rewardConfig={{
                bookingSourceField: rewardBookingSourceField,
                bookingSourceName: "rewardBookingSource",
                bookingSourceOptions: bookingSourceOptions,
                rideTypeField: rideTypeField,
                rideTypeName: "rideType",
                paymentTypeField: rewardPaymentTypeField,
                paymentTypeName: "rewardPaymentType",
                paymentTypeOptions: paymentTypeOptions,
                minDistanceField: rewardMinDistanceField,
                minDistanceName: "rewardMinDistance",
                tooltipId: "rewardMinDistanceInfo",
              }}
              errors={errors}
              trigger={trigger}
            />
          )}
        </Box>

        <div className="d-flex justify-content-end mt-4 gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleCloseWithWarning}
          >
            Cancel
          </button>
          {activeTab === 0 && (
            <button
              type="button"
              className="btn btn-primary"
              style={{ backgroundColor: "#41CC7D", borderColor: "#41CC7D" }}
              onClick={handleNext}
            >
              Next
            </button>
          )}
          {activeTab === 1 && (
            <>
              <button
                type="button"
                className="btn btn-outline-primary"
                style={{ color: "#41CC7D", borderColor: "#41CC7D" }}
                onClick={() => setActiveTab(0)}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ backgroundColor: "#41CC7D", borderColor: "#41CC7D" }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : offerObj ? "Update" : "Create"}
              </button>
            </>
          )}
        </div>
      </Form>

      {/* Unsaved Changes Warning Dialog */}
      <Dialog disableScrollLock open={showCloseWarning} onClose={() => setShowCloseWarning(false)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes that haven't been submitted yet.
          </Typography>
          <Typography sx={{ mt: 2, fontWeight: 500 }}>
            Are you sure you want to leave? All entered data will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseWarning(false)} sx={{ color: '#666' }}>
            Stay
          </Button>
          <Button
            onClick={confirmClose}
            variant="contained"
            sx={{ backgroundColor: '#dc3545', '&:hover': { backgroundColor: '#c82333' } }}
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        isOpen={showExpiredSaveWarning}
        isLoading={isSaving}
        toggler={() => {
           setShowExpiredSaveWarning(false);
           setPendingSubmitData(null);
        }}
        onConfirm={handleConfirmExpiredSave}
        title="Warning: Expired Offer"
        message={
          <span style={{ display: 'block' }}>
            <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Saving Expired Offer</span> 
            <br /><br />
            This offer's validity period has ended. If you save these changes, the offer will remain expired and will not be applied to any bookings because the 'Valid Upto' date is in the past.
            <br /><br />
            Are you sure you want to save it anyway?
          </span>
        }
      />
    </CommonModal>
  );
};

export default OfferDialog;
